/** QuickPlan — Notice de montage façon IKEA : couverture, inventaire repéré (A, B, C…),
 *  plan de coupe des panneaux (formats du fournisseur), plans de perçage cotés, étapes illustrées (pièces déjà montées en gris, pièces de
 *  l'étape en couleur, vis sorties de leur trou). Export PDF direct et impression. */
import { useEffect, useMemo, useState } from 'react';
import { useProjectStore } from '../store/useProjectStore.js';
import ExportGlbButton from './ExportGlbButton.jsx';
import ExportStepButton from './ExportStepButton.jsx';
import ExportStlButton from './ExportStlButton.jsx';
import { computeNotice } from '../../shared/notice.js';
import { computeCutPlan, sawCutsLabel } from '../../shared/cutting.js';
import SheetStock from './SheetStock.jsx';
import { templateSheets, sheetRange } from '../notice/templates.js';
import { piecePrep, tooling, PREP_TIPS } from '../notice/prep.js';
import { projectBounds } from '../geom.js';
import { renderIllustration, NOTICE_VIEWS, OPPOSITE_VIEW } from '../notice/render3d.js';
import { faceDrawing, sheetDrawing, holeSummary, holeText, recessSummary, recessText, fmt } from '../notice/drawing.js';

/** Illustrations générées une à une (l'interface reste fluide), refaites si la notice change. */
function useNoticeImages(project, lookup, notice, view, explode) {
  const [state, setState] = useState({ images: null, done: 0, total: 0 });
  useEffect(() => {
    let cancelled = false;
    const yieldUi = () => new Promise((r) => setTimeout(r, 0));
    const all = project.instances.map((i) => i.id);
    const total = 3 + notice.pieces.length + notice.steps.length;
    const out = { cover: null, overview: [], thumbs: {}, steps: [] };
    setState({ images: null, done: 0, total });
    (async () => {
      try {
        let done = 0;
        const step = async (fn) => {
          await yieldUi();
          if (cancelled) throw new Error('annulé');
          fn();
          done++;
          setState({ images: done === total ? { ...out } : null, done, total });
        };
        await step(() => { out.cover = renderIllustration(project, lookup, { ids: all, allNew: true, view, width: 1600, height: 1100 }); });
        // Vue d'ensemble repérée : la vue choisie et la vue opposée (pièces cachées de face).
        for (const v of [view, OPPOSITE_VIEW[view]]) {
          await step(() => { out.overview.push({ view: v, ...renderIllustration(project, lookup, { ids: all, allNew: true, letters: notice.letterOf, labelAll: true, view: v, width: 1600, height: 1100 }) }); });
        }
        for (const p of notice.pieces) {
          await step(() => { out.thumbs[p.letter] = renderIllustration(project, lookup, { ids: [p.sampleId], solo: true, allNew: true, view, width: 520, height: 360 }); });
        }
        for (const s of notice.steps) {
          await step(() => {
            const fresh = new Set(s.newIds);
            out.steps.push(renderIllustration(project, lookup, {
              ids: [...s.prevIds, ...s.newIds],
              newIds: fresh,
              fixings: notice.fixings.filter((f) => fresh.has(f.instId)),
              letters: notice.letterOf,
              view,
              explode,
            }));
          });
        }
      } catch (e) {
        if (!cancelled) setState((st) => ({ ...st, error: e.message }));
      }
    })();
    return () => { cancelled = true; };
  }, [notice, view, explode]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}

function Illustration({ img, className = '' }) {
  if (!img) return <div className={`ill placeholder ${className}`}><span className="spinner" /></div>;
  return (
    <div className={`ill ${className}`}>
      <img src={img.url} alt="" />
      {img.labels?.map((l, k) => <span key={k} className="ill-label" style={{ left: `${l.x * 100}%`, top: `${l.y * 100}%` }}>{l.text}</span>)}
    </div>
  );
}

function Badge({ letter, big }) {
  return <span className={`nbadge ${big ? 'big' : ''}`}>{letter}</span>;
}

/** Liste de primitives de drawing.js → SVG (mêmes unités que le PDF : mm). */
function DrawingSvg({ d, px = 3.4, className = 'face-svg' }) {
  return (
    <svg className={className} viewBox={`0 0 ${d.w} ${d.h}`} style={{ width: d.w * px }}>
      {d.items.map((it, k) => {
        if (it.t === 'rect') return <rect key={k} x={it.x} y={it.y} width={it.w} height={it.h} fill={it.fill ?? 'none'} stroke={it.stroke ?? '#111'} strokeWidth={it.lw} strokeDasharray={it.dash?.join(' ')} />;
        if (it.t === 'circle') return <circle key={k} cx={it.cx} cy={it.cy} r={it.r} fill={it.fill ?? 'none'} stroke={it.stroke ?? '#111'} strokeWidth={it.lw} />;
        if (it.t === 'line') return <line key={k} x1={it.x1} y1={it.y1} x2={it.x2} y2={it.y2} stroke={it.color ?? it.stroke ?? '#111'} strokeWidth={it.lw} strokeDasharray={it.dash?.join(' ')} />;
        return (
          <text key={k} x={it.x} y={it.y} fontSize={it.size} textAnchor={it.anchor ?? 'start'} fontWeight={it.bold ? 700 : 400} fill={it.color ?? '#111'}
            transform={it.rot === 90 ? `rotate(-90 ${it.x} ${it.y})` : undefined}>{it.s}</text>
        );
      })}
      {d.stretched && <title>Épaisseur dilatée pour la lisibilité</title>}
    </svg>
  );
}

function FaceSvg({ face }) {
  const d = useMemo(() => faceDrawing(face, 125, 95), [face]);
  return <DrawingSvg d={d} />;
}

const money = (v) => v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function CutPlan({ cut }) {
  return (
    <section className="sheet">
      <h2>Plan de coupe des panneaux</h2>
      <p className="dim">Coupes guillotine (scie à panneaux), trait de scie {fmt(cut.kerf)} mm, rognage {fmt(cut.trim)} mm par bord sauf mention. À remettre au service de découpe du magasin.</p>
      <SheetStock cut={cut} />
      {!cut.groups.length && <p className="empty">Aucun panneau à découper dans ce projet.</p>}
      {cut.groups.map((g) => (
        <div key={g.key} className="cut-group">
          <div className="drill-head">
            <b>{g.material} — ép. {fmt(g.thickness)} mm</b>
            <span className="grow" />
            <span className="dim">
              {fmt(g.format.length)} × {fmt(g.format.width)}{g.format.trim !== cut.trim && ` (rognage ${fmt(g.format.trim)})`} · <b>{g.sheets.length} panneau{g.sheets.length > 1 ? 'x' : ''}</b> · rendement {fmt(g.usage)} %{g.cost != null && <> · {money(g.cost)} €</>} · <b>{sawCutsLabel(g.cuts, g.trimCuts, g.cutLength)}</b>
            </span>
          </div>
          {g.defaultFormat && <div className="paper-warn noprint">Format par défaut : ajoutez le format de votre fournisseur pour cette épaisseur ci-dessus.</div>}
          {g.oversize.length > 0 && <div className="paper-warn">⚠ Trop grandes pour le format : {g.oversize.map((p) => `${p.letter || p.name} ${fmt(p.L)} × ${fmt(p.W)}`).join(', ')}</div>}
          <table className="hw-table cut-list">
            <thead><tr><th>Repère</th><th>Désignation</th><th className="n">Longueur</th><th className="n">Largeur</th><th className="n">Qté</th></tr></thead>
            <tbody>
              {g.list.map((r) => (
                <tr key={`${r.letter}-${r.L}-${r.W}`}><td>{r.letter ? <span className="nbadge">{r.letter}</span> : '–'}</td><td>{r.name}</td><td className="n">{fmt(r.L)}</td><td className="n">{fmt(r.W)}</td><td className="n qty">{r.qty}</td></tr>
              ))}
            </tbody>
          </table>
          {g.sheets.map((sh) => (
            <div key={sh.index} className="cut-sheet">
              <div className="face-title">Panneau {sh.index} / {g.sheets.length} — {sh.placements.length} pièce{sh.placements.length > 1 ? 's' : ''}, {sawCutsLabel(sh.cuts + sh.trimCuts, sh.trimCuts)}, rendement {fmt(sh.usage)} %</div>
              <DrawingSvg d={sheetDrawing(sh, g.format, g.format.trim, 186, 140)} px={4.6} className="sheet-svg" />
            </div>
          ))}
        </div>
      ))}
      {cut.groups.length > 0 && (
        <p className="cut-total">
          Total : <b>{cut.groups.reduce((a, g) => a + g.sheets.length, 0)} panneau(x) / planche(s)</b> · <b>{sawCutsLabel(cut.cuts, cut.trimCuts, cut.cutLength)}</b>
          {cut.groups.length > 1 && !cut.unpriced && <> · <b>{money(cut.total)} €</b></>}
        </p>
      )}
    </section>
  );
}

export default function NoticeView() {
  const project = useProjectStore((s) => s.project);
  const library = useProjectStore((s) => s.library);
  const setSelection = useProjectStore((s) => s.setSelection);
  const setMode = useProjectStore((s) => s.setMode);
  const updateProject = useProjectStore((s) => s.updateProject);
  const readOnly = project.role === 'viewer';
  const lookup = useMemo(() => useProjectStore.getState().lookup(), [project, library]); // eslint-disable-line react-hooks/exhaustive-deps
  const notice = useMemo(() => computeNotice(project, lookup), [project, lookup]);
  const cut = useMemo(() => computeCutPlan(project, lookup, notice.letterOf), [project, lookup, notice]);
  const sheets = useMemo(() => templateSheets(notice), [notice]);
  const bounds = useMemo(() => projectBounds(project, lookup), [project, lookup]);
  const [view, setViewLocal] = useState(project.settings?.noticeView ?? 'av-d');
  const [explode, setExplodeLocal] = useState(project.settings?.noticeExplode ?? false);
  const { images, done, total, error } = useNoticeImages(project, lookup, notice, view, explode);
  const [exporting, setExporting] = useState(false);

  const setting = (key, value, local) => {
    local(value);
    if (!readOnly) updateProject((p) => { p.settings[key] = value; });
  };
  const show = (ids) => { setSelection([...new Set(ids)]); setMode('design'); };
  const pieceByLetter = new Map(notice.pieces.map((p) => [p.letter, p]));
  const drilled = notice.pieces.filter((p) => p.faces.length);
  const nPieces = notice.pieces.reduce((s, p) => s + p.qty, 0);

  const exportTemplates = async () => {
    setExporting(true);
    try {
      const [{ buildTemplatesPdf }, { noticeFileName }] = await Promise.all([import('../notice/templatesPdf.js'), import('../notice/pdf.js')]);
      buildTemplatesPdf({ project, notice }).save(noticeFileName(project, 'gabarits-percage'));
    } catch (e) {
      useProjectStore.getState().setStatus(`⚠ Export des gabarits : ${e.message}`);
    } finally {
      setExporting(false);
    }
  };

  const exportPdf = async (only = null) => {
    setExporting(true);
    try {
      const { buildNoticePdf, noticeFileName } = await import('../notice/pdf.js');
      const doc = buildNoticePdf({ project, notice, cut, images, bounds, only });
      doc.save(noticeFileName(project, only === 'cut' ? 'plan-de-coupe' : 'notice'));
    } catch (e) {
      useProjectStore.getState().setStatus(`⚠ Export PDF : ${e.message}`);
    } finally {
      setExporting(false);
    }
  };

  if (!project.instances.length) {
    return <div className="notice"><div className="sheet"><h2>Notice de montage</h2><p className="empty">Le projet est vide : posez des pièces dans la vue 3D pour obtenir leur notice.</p></div></div>;
  }

  return (
    <div className="notice">
      <div className="notice-toolbar noprint">
        <label>Vue
          <select value={view} onChange={(e) => setting('noticeView', e.target.value, setViewLocal)}>
            {Object.entries(NOTICE_VIEWS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </label>
        <label className="row"><input type="checkbox" checked={explode} onChange={(e) => setting('noticeExplode', e.target.checked, setExplodeLocal)} /> Pièces éclatées</label>
        <span className="grow" />
        {!images && !error && <span className="muted"><span className="spinner" /> Illustrations {done}/{total}</span>}
        {error && <span className="warn">⚠ {error}</span>}
        <button disabled={!sheets.length || exporting} onClick={exportTemplates} title="Patrons de perçage à l'échelle 1:1 à découper et scotcher sur les pièces, réglettes pour les lignes de trous">⤓ Gabarits de perçage ({sheets.length})</button>
        <button disabled={!cut.groups.length || exporting} onClick={() => exportPdf('cut')} title="Liste de débit et calepinage seuls, pour le service de découpe">⤓ Plan de coupe PDF</button>
        <button className="primary" disabled={!images || exporting} onClick={() => exportPdf()}>{exporting ? '…' : '⤓ Export PDF'}</button>
        <ExportGlbButton />
        <ExportStepButton />
        <ExportStlButton />
        <button disabled={!images} onClick={() => window.print()}>🖨 Imprimer</button>
      </div>

      {notice.errors.length > 0 && <div className="warn-box noprint">{notice.errors.map((e) => <div key={e}>⚠ {e}</div>)}</div>}
      {notice.noSteps && (
        <div className="info-box noprint">
          Aucune étape de montage n'est définie : toutes les pièces sont montrées en une seule étape.
          Sélectionnez des pièces dans la vue 3D puis <b>Propriétés → 🔢 Étape…</b> pour découper le montage (ou demandez-le à l'assistant).
        </div>
      )}

      {/* ── Couverture ── */}
      <section className="sheet cover">
        <div className="kicker">Notice de montage</div>
        <h1>{project.name}</h1>
        <div className="meta">
          {[project.meta?.client && `Client : ${project.meta.client}`, project.meta?.reference && `Réf. ${project.meta.reference}`, project.meta?.author && `Auteur : ${project.meta.author}`, new Date().toLocaleDateString('fr-FR')].filter(Boolean).join('   ·   ')}
        </div>
        <Illustration img={images?.cover} className="cover-img" />
        <div className="stats">
          <div><b>{nPieces}</b><span>pièce{nPieces > 1 ? 's' : ''} ({notice.pieces.length} repère{notice.pieces.length > 1 ? 's' : ''})</span></div>
          <div><b>{notice.steps.length}</b><span>étape{notice.steps.length > 1 ? 's' : ''}</span></div>
          <div><b>{notice.holeCount + notice.recessCount}</b><span>perçage{notice.holeCount > 1 ? 's' : ''}{notice.recessCount > 0 && ` et encoche${notice.recessCount > 1 ? 's' : ''}`}</span></div>
          <div><b>{Math.round(notice.hardware.reduce((s, h) => s + h.qty, 0))}</b><span>éléments de quincaillerie</span></div>
        </div>
        {bounds && <p className="dim">Encombrement hors tout : {[0, 1, 2].map((k) => fmt(bounds.max[k] - bounds.min[k])).join(' × ')} mm (L × P × H)</p>}
      </section>

      {/* ── Inventaire ── */}
      <section className="sheet">
        <h2>Inventaire des pièces</h2>
        <div className="inv-grid">
          {notice.pieces.map((p) => (
            <div key={p.key} className="inv-card" title={`${p.name} — ${p.instances.length} pièce(s)`}>
              <Illustration img={images?.thumbs[p.letter]} className="thumb" />
              <div className="inv-line"><Badge letter={p.letter} /><span className="qty">× {p.qty}</span>
                <button className="icon noprint" title="Sélectionner dans la vue 3D" onClick={() => show(p.instances)}>◎</button>
              </div>
              <div className="inv-name">{p.name}</div>
              <div className="inv-dim">{p.label || p.size.map(fmt).join(' × ')}{p.holeCount > 0 && ` · ${p.holeCount} trou${p.holeCount > 1 ? 's' : ''}`}{p.recessCount > 0 && ` · ${p.recessCount} encoche${p.recessCount > 1 ? 's' : ''}`}</div>
            </div>
          ))}
        </div>
        {notice.hardware.length > 0 && (
          <>
            <h3 className="paper-h3">Quincaillerie</h3>
            <table className="hw-table">
              <tbody>
                {notice.hardware.map((h) => (
                  <tr key={h.ref}><td className="n qty">× {fmt(h.qty)}</td><td>{h.name}</td><td className="ref">{h.ref}</td></tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </section>

      {/* ── Vue d'ensemble repérée ── */}
      <section className="sheet">
        <h2>Vue d'ensemble repérée</h2>
        <p className="dim">Chaque pièce porte la lettre de l'inventaire. La seconde vue montre l'ensemble de l'autre côté.</p>
        <div className="overview">
          {[0, 1].map((k) => {
            const img = images?.overview?.[k];
            return (
              <div key={k}>
                <div className="face-title">{NOTICE_VIEWS[img?.view ?? (k ? OPPOSITE_VIEW[view] : view)].label}</div>
                <Illustration img={img} />
                {img?.hidden?.length > 0 && <div className="dim">Non visibles sur cette vue : {img.hidden.join(', ')}</div>}
              </div>
            );
          })}
        </div>
        <div className="overview-legend">
          {notice.pieces.map((p) => <span key={p.key}><Badge letter={p.letter} /> {p.name} <b>× {p.qty}</b></span>)}
        </div>
      </section>

      {/* ── Plan de coupe ── */}
      <CutPlan cut={cut} />

      {/* ── Préparation des pièces ── */}
      {drilled.length > 0 && (
        <section className="sheet">
          <h2>Préparation des pièces</h2>
          <ul className="prep-tips">{PREP_TIPS.map((t) => <li key={t}>{t}</li>)}</ul>
          <h3 className="paper-h3">Outillage</h3>
          <ul className="prep-tools">{tooling(notice).map((t) => <li key={t}>{t}</li>)}</ul>
          {drilled.map((p) => (
            <div key={p.key} className="prep-piece">
              <div className="drill-head"><Badge letter={p.letter} /><b>{p.name}</b><span className="qty">× {p.qty}</span><span className="grow" /><span className="dim">{p.size.map(fmt).join(' × ')} mm</span></div>
              {piecePrep(p, sheets).map((f) => (
                <div key={f.face} className="prep-face">
                  <b>{f.label}</b>{f.sheets && <span className="dim"> — {f.sheets}</span>}
                  <ul>{f.lines.map((l) => <li key={l}>{l}</li>)}</ul>
                </div>
              ))}
            </div>
          ))}
          <p className="dim">Les gabarits (G…) et réglettes (R…) se téléchargent avec « ⤓ Gabarits de perçage » : {sheets.filter((s) => s.kind === 'window').length} gabarit(s) à découper, {sheets.filter((s) => s.kind === 'ruler').length} réglette(s), imprimés à l'échelle 1:1.</p>
        </section>
      )}

      {/* ── Plans de perçage ── */}
      {drilled.length > 0 && (
        <section className="sheet">
          <h2>Plans de perçage</h2>
          <p className="dim">Cotes en mm depuis le coin bas-gauche de chaque face, vue de l'extérieur. Cercle barré = traversant ; disque gris = borgne (profondeur indiquée) ; hachures = encoche (coin bas-gauche entre crochets).</p>
          {drilled.map((p) => (
            <div key={p.key} className="drill-piece">
              <div className="drill-head">
                <Badge letter={p.letter} big />
                <b>{p.name}</b><span className="qty">× {p.qty}</span>
                <span className="grow" />
                <span className="dim">{p.size.map(fmt).join(' × ')} mm{p.material ? ` · ${p.material}` : ''}</span>
                <button className="icon noprint" title="Sélectionner dans la vue 3D" onClick={() => show(p.instances)}>◎</button>
              </div>
              {p.faces.map((f) => (
                <div key={f.code} className="drill-face">
                  <div className="face-title">{f.label} — {fmt(f.size[0])} × {fmt(f.size[1])} mm <span className="dim">· {sheetRange(sheets, p, f.code)}</span></div>
                  <div className="face-body">
                    <FaceSvg face={f} />
                    <div className="face-side">
                      <b>{[f.holes.length > 0 && `${f.holes.length} trou${f.holes.length > 1 ? 's' : ''}`, f.recesses?.length > 0 && `${f.recesses.length} encoche${f.recesses.length > 1 ? 's' : ''}`].filter(Boolean).join(' · ')}</b>
                      {holeSummary(f.holes).map((s) => <div key={`${s.dia}-${s.through}-${s.depth}`}>{s.count} × {holeText(s)}</div>)}
                      {recessSummary(f.recesses).map((r) => <div key={recessText(r)}>{r.count} × {recessText(r)}</div>)}
                      <div className="coords">{[...f.holes.map((h) => `(${fmt(h.u)} ; ${fmt(h.v)})`), ...(f.recesses ?? []).map((r) => `[${fmt(r.u)} ; ${fmt(r.v)}]`)].join('  ')}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </section>
      )}

      {/* ── Étapes ── */}
      <section className="sheet steps">
        <h2>Montage</h2>
        {notice.steps.map((s, i) => (
          <div key={s.index} className="step">
            <div className="step-head">
              <span className="step-num">{s.index}</span>
              <div>
                <b>{s.unassigned ? 'Éléments sans étape' : `Étape ${s.index}`}</b>
                <div className="dim">{s.pieces.map((x) => `${x.qty} × ${x.letter}`).join('   ') || 'quincaillerie seule'}</div>
              </div>
              <span className="grow" />
              <button className="icon noprint" title="Sélectionner les pièces de l'étape dans la vue 3D" onClick={() => show(s.newIds)}>◎</button>
            </div>
            <div className="step-body">
              <Illustration img={images?.steps[i]} className="step-img" />
              <div className="step-side">
                {s.pieces.map((x) => (
                  <div key={x.letter} className="step-piece">
                    <Illustration img={images?.thumbs[x.letter]} className="mini" />
                    <Badge letter={x.letter} /><span className="qty">× {x.qty}</span>
                    <div className="step-piece-name">{pieceByLetter.get(x.letter)?.name}</div>
                  </div>
                ))}
                {s.hardware.length > 0 && (
                  <div className="step-hw">
                    {s.hardware.map((h) => <div key={h.ref}><b>× {fmt(h.qty)}</b> {h.name}</div>)}
                  </div>
                )}
              </div>
            </div>
            {s.notes.length > 0 && <div className="step-notes">{s.notes.map((n, k) => <div key={k}><b>{n.name}</b> : {n.note}</div>)}</div>}
          </div>
        ))}
      </section>
    </div>
  );
}
